<script setup lang="ts">
import { Button, Dialog, Divider, useScreenSize } from "@soft-db/ui";
import { watch } from "vue";
import { useI18n } from "vue-i18n";

const dialog = defineModel<boolean>();

defineProps<{
	actionText?: string;
	actionDisabled?: boolean;
	hideAction?: boolean;
	actionLoading?: boolean;
}>();

const emits = defineEmits<{
	(e: "close"): void;
	(e: "click:action"): void;
}>();

defineSlots<{
	title(): void;
	content(): void;
	"left-action"(): void;
	action(): void;
	close(): void;
}>();

const { t } = useI18n();

const { mdOrLower } = useScreenSize();

watch(dialog, () => {
	if (!dialog.value) emits("close");
});

function actionClick() {
	emits("click:action");
}

function closeDialog() {
	dialog.value = false;
}
</script>

<template>
	<!-- We disable dialog padding, since we roll our own flex-box container to allow vertical scroll with sticky title and action -->
	<Dialog v-model="dialog" class="overflow-y-hidden p-0" :fullScreen="mdOrLower" closeButton>
		<template #default>
			<div class="flex h-full max-h-[inherit] flex-col overflow-hidden p-4">
				<div class="flex shrink-0">
					<h2 class="inline-flex grow items-center gap-2">
						<slot name="title" />
					</h2>
					<Divider class="m-0!" />
				</div>

				<!-- The negative margin and padding is used to prevent overflow to clip outline element inside this div -->
				<div class="-mx-3 min-h-0 flex-1 overflow-y-auto px-3 py-2">
					<slot name="content" />
				</div>

				<div v-if="!hideAction" class="shrink-0">
					<Divider class="m-0!" />
					<div class="flex gap-2">
						<slot name="left-action" />
						<div class="grow" />
						<slot name="close">
							<Button color="default" variant="ghost" @click="closeDialog">
								{{ t("common.close") }}
							</Button>
						</slot>
						<slot name="action">
							<Button
								v-if="actionText"
								color="secondary"
								variant="ghost"
								:disabled="actionDisabled"
								:loading="actionLoading"
								@click="actionClick"
							>
								{{ actionText }}
							</Button>
						</slot>
					</div>
				</div>
			</div>
		</template>
	</Dialog>
</template>
